import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Users, DollarSign, CheckCircle, Clock, LogOut, Copy, Check, Link2, Ban, Info, Printer } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import HelperPayoutDetails from './dashboard/HelperPayoutDetails';
import AffiliateEarnings from '../components/AffiliateEarnings';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_CONFIG = {
  lead: { label: 'Lead', color: 'bg-gray-100 text-gray-700 border-gray-200', icon: Clock },
  signed_up: { label: 'Signed Up', color: 'bg-amber-100 text-amber-800 border-amber-200', icon: Clock },
  paid: { label: 'Paid', color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle },
  pending_payout: { label: 'Pending Payout', color: 'bg-amber-100 text-amber-800 border-amber-200', icon: Clock },
  approved: { label: 'Approved', color: 'bg-purple-100 text-purple-800 border-purple-200', icon: CheckCircle },
  paid_out: { label: 'Paid Out', color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle },
  void: { label: 'Void', color: 'bg-red-50 text-red-600 border-red-200', icon: Ban },
};

const HelperDashboard = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const token = localStorage.getItem('helper_token');
  const helperName = localStorage.getItem('helper_name');

  useEffect(() => {
    if (!token) { navigate('/helper/login'); return; }
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const res = await axios.get(`${API}/helper/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
      // Iter 170 — one-time "first founder lead converted" toast. Backend
      // returns `firstFounderConversionToastDue: true` once per helper account
      // (gated by `firstConversionToastSeenAt`). Fire & immediately mark seen.
      if (res.data?.summary?.firstFounderConversionToastDue) {
        toast.success(
          t(
            'helper.founderConversionToast',
            '🎉 Your first founder lead converted! Commission is now pending.'
          ),
          { duration: 8000, id: 'founder-conversion-toast' }
        );
        axios.post(
          `${API}/helper/dashboard/founder-conversion-toast-seen`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        ).catch(() => {});
      }
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem('helper_token');
        navigate('/helper/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('helper_token');
    localStorage.removeItem('helper_name');
    localStorage.removeItem('helper_code');
    navigate('/helper/login');
  };

  const copyLink = () => {
    const code = data?.helper?.referralCode || localStorage.getItem('helper_code');
    navigator.clipboard.writeText(`https://www.find-2-day.com/business/signup?ref=${code}`).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Loading dashboard...</p>
      </div>
    );
  }

  if (!data) return null;

  const { helper, summary, referrals } = data;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-purple-600 to-purple-700 text-white px-4 py-5">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div>
            <p className="text-purple-200 text-xs font-medium uppercase tracking-wider">Helper Dashboard</p>
            <h1 className="text-xl font-bold mt-0.5" data-testid="helper-welcome">Welcome, {helper.name}</h1>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg text-sm font-medium transition-colors" data-testid="helper-logout-btn">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto p-4 space-y-6">
        {/* Clarity banner — same style as Affiliate Program landing page */}
        <div className="inline-flex flex-wrap items-center gap-2" data-testid="helper-clarity-banner">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-purple-200 text-purple-800 text-xs sm:text-sm font-medium shadow-sm" data-testid="helper-clarity-confirmed">
            <Clock className="h-3.5 w-3.5" />
            {t('affiliateClarityConfirmed', { days: summary.cooldownDays || 30, defaultValue: `Confirmed in ${summary.cooldownDays || 30} days` })}
          </span>
          <span className="text-gray-300 text-xs select-none" aria-hidden="true">·</span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-purple-200 text-purple-800 text-xs sm:text-sm font-medium shadow-sm" data-testid="helper-clarity-paid">
            <DollarSign className="h-3.5 w-3.5" />
            {t('affiliateClarityPaid', { amount: (summary.minPayoutEur || 20).toFixed(0), defaultValue: `Paid from €${(summary.minPayoutEur || 20).toFixed(0)}` })}
          </span>
        </div>

        {/* Referral Link */}
        <div className="bg-white rounded-xl shadow-sm border p-4 flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-xs text-gray-500 font-medium mb-1">Your Referral Link</p>
            <code className="text-sm text-purple-700 font-mono break-all" data-testid="helper-referral-link">
              find-2-day.com/business/signup?ref={helper.referralCode}
            </code>
          </div>
          <button
            onClick={copyLink}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              copied ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-purple-50 text-purple-700 border border-purple-200 hover:bg-purple-100'
            }`}
            data-testid="helper-copy-link-btn"
          >
            {copied ? <><Check className="h-4 w-4" /> Copied!</> : <><Copy className="h-4 w-4" /> Copy Link</>}
          </button>
          <button
            onClick={() => window.open(`/partners/poster?ref=${encodeURIComponent(helper.referralCode || '')}`, '_blank')}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100 transition-colors"
            data-testid="helper-print-poster-btn"
          >
            <Printer className="h-4 w-4" /> Print my poster
          </button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Users className="h-4 w-4 text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">{t('helper.foundersReferred', 'Founder referrals')}</p>
                <p className="text-xl font-bold text-purple-600" data-testid="helper-founder-referrals">{summary.founderReferralCount || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-4 w-4 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">{t('helper.paidReferrals', 'Paid referrals')}</p>
                <p className="text-xl font-bold text-green-600" data-testid="helper-total-paid">{summary.paidReferralCount ?? summary.totalPaid}</p>
              </div>
            </div>
          </div>
          {/* Iter 167 — analytics-only WhatsApp tracking. Counts signups
              from the affiliate's WhatsApp share link (?wa=1 marker). */}
          <div className="bg-white rounded-xl shadow-sm border p-4" data-testid="helper-whatsapp-card">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-[#25D366]/10 rounded-lg">
                <span className="text-[#25D366] text-base leading-none" aria-hidden="true">💬</span>
              </div>
              <div>
                <p className="text-xs text-gray-500">{t('helper.whatsappSignups', 'WhatsApp signups')}</p>
                <p className="text-xl font-bold text-[#1ebe5d]" data-testid="helper-whatsapp-signups">
                  {summary.whatsappSignupCount || 0}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock className="h-4 w-4 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">{t('pendingMaturing', 'Pending (maturing)')}</p>
                <p className="text-xl font-bold text-amber-600" data-testid="helper-maturing-commission">{(summary.totalCommissionMaturing || 0).toFixed(2)} EUR</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <DollarSign className="h-4 w-4 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">{t('availableForPayout', 'Available for payout')}</p>
                <p className="text-xl font-bold text-emerald-600" data-testid="helper-available-commission">{(summary.totalCommissionAvailable || 0).toFixed(2)} EUR</p>
              </div>
            </div>
          </div>
        </div>

        {/* Two-bucket detail cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* Pending (maturing) */}
          <div className="bg-white rounded-xl shadow-sm border border-amber-100 p-5" data-testid="pending-maturing-card">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="flex items-center gap-1.5">
                  <Clock className="h-4 w-4 text-amber-600" />
                  <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold">{t('pendingMaturing', 'Pending (maturing)')}</p>
                  <span
                    className="relative group cursor-help"
                    data-testid="clearance-tooltip-trigger"
                  >
                    <Info className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600" />
                    <span
                      className="absolute left-1/2 -translate-x-1/2 top-full mt-2 z-50 w-64 p-2.5 bg-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 pointer-events-none transition-opacity"
                      data-testid="clearance-tooltip-content"
                    >
                      {t('clearanceTooltip', { days: summary.cooldownDays || 30, defaultValue: `Commissions become available ${summary.cooldownDays || 30} days after payment clears, in line with our payment processor's chargeback window. You'll still earn the full amount — it just takes ${summary.cooldownDays || 30} days to mature.` })}
                    </span>
                  </span>
                </div>
                <p className="text-2xl font-extrabold text-amber-600 tabular-nums mt-1" data-testid="pending-maturing-amount">
                  {(summary.totalCommissionMaturing || 0).toFixed(2)} €
                </p>
              </div>
            </div>
            <p className="text-xs text-gray-600 mt-2">{t('pendingMaturingDesc', { days: summary.cooldownDays || 30, defaultValue: `Commissions waiting for the ${summary.cooldownDays || 30}-day clearance window.` })}</p>
          </div>

          {/* Available for payout */}
          <div className="bg-white rounded-xl shadow-sm border border-emerald-100 p-5" data-testid="available-payout-card">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="flex items-center gap-1.5">
                  <CheckCircle className="h-4 w-4 text-emerald-600" />
                  <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold">{t('availableForPayout', 'Available for payout')}</p>
                </div>
                <p className="text-2xl font-extrabold text-emerald-600 tabular-nums mt-1" data-testid="available-payout-amount">
                  {(summary.totalCommissionAvailable || 0).toFixed(2)} €
                </p>
              </div>
              <div className="text-right">
                <p className="text-[11px] text-gray-500">{t('balanceLabel', 'Balance')} / {(summary.minPayoutEur || 20).toFixed(0)} € {t('min', 'min')}</p>
              </div>
            </div>
            {(() => {
              const available = summary.totalCommissionAvailable || 0;
              const minPayout = summary.minPayoutEur || 20;
              const pct = Math.min(100, (available / minPayout) * 100);
              const eligible = available >= minPayout;
              return (
                <>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden mt-3">
                    <div
                      className={`h-full ${eligible ? 'bg-emerald-500' : 'bg-purple-500'} transition-all`}
                      style={{ width: `${pct}%` }}
                      data-testid="payout-progress-bar"
                    />
                  </div>
                  <p className={`text-xs mt-2 font-medium ${eligible ? 'text-emerald-700' : 'text-gray-600'}`} data-testid="payout-eligibility">
                    {eligible ? t('eligibleForPayout', 'Eligible for payout') : t('notYetEligible', 'Not yet eligible — keep going!')}
                  </p>
                </>
              );
            })()}
            <p className="text-xs text-gray-600 mt-2">{t('availableForPayoutDesc', { days: summary.cooldownDays || 30, defaultValue: `Cleared the ${summary.cooldownDays || 30}-day window and ready to be paid out.` })}</p>
            {/* Lifetime renewal earnings — always visible so helpers know this exists
                even before they've earned their first one. */}
            <div className="mt-3 flex flex-col gap-1" data-testid="renewal-commission-summary">
              <div className="flex items-center gap-2 text-xs">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200 font-medium">
                  {t('lifetimeRenewalEarnings', 'Lifetime renewal earnings')}: €{(summary.renewalCommissionTotal || 0).toFixed(2)}
                </span>
                {(summary.renewalCount || 0) > 0 && (
                  <span className="text-[11px] text-gray-500">
                    {t('renewalEarnings', { count: summary.renewalCount, total: (summary.renewalCommissionTotal || 0).toFixed(2), defaultValue: `${summary.renewalCount} renewal · €${(summary.renewalCommissionTotal || 0).toFixed(2)}` })}
                  </span>
                )}
              </div>
              {(summary.renewalCount || 0) === 0 && (
                <p className="text-[11px] text-gray-500" data-testid="renewal-commission-hint">
                  {t('lifetimeRenewalEarningsHint', "You'll start earning these once your referred businesses renew. Nothing extra to do — we track it for you.")}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Per-plan earnings breakdown */}
        <AffiliateEarnings />

        {/* Collapsible "How payouts work" panel — closed by default */}
        <details className="bg-white rounded-xl shadow-sm border border-gray-200 mt-6 overflow-hidden" data-testid="how-payouts-work-panel">
          <summary className="px-5 py-4 cursor-pointer select-none list-none flex items-center justify-between hover:bg-gray-50 transition-colors" data-testid="how-payouts-work-toggle">
            <span className="text-sm font-semibold text-gray-900">{t('howPayoutsWorkTitle', 'How payouts work')}</span>
            <span className="text-xs text-purple-600 font-medium" aria-hidden>›</span>
          </summary>
          <div className="px-5 pb-5 pt-1 border-t border-gray-100">
            <ul className="space-y-2 text-sm text-gray-700" data-testid="how-payouts-work-rules">
              <li className="flex gap-2"><span aria-hidden className="text-purple-600">•</span><span>{t('howPayoutsRule1', 'You earn 50% of everything a business you referred pays — subscriptions, renewals, boosts, and any future paid product.')}</span></li>
              <li className="flex gap-2"><span aria-hidden className="text-purple-600">•</span><span>{t('howPayoutsRule2', 'One rule, no exceptions. As long as they stay with Find-2-Day, you keep earning 50% every time they pay.')}</span></li>
              <li className="flex gap-2"><span aria-hidden className="text-purple-600">•</span><span>{t('howPayoutsRule3', { days: summary.cooldownDays || 30, defaultValue: `New earnings are pending for ${summary.cooldownDays || 30} days while we wait for the payment to clear with the bank.` })}</span></li>
              <li className="flex gap-2"><span aria-hidden className="text-purple-600">•</span><span>{t('howPayoutsRule4', { days: summary.cooldownDays || 30, defaultValue: `If the payment fails, is refunded, cancelled, or charged back during those ${summary.cooldownDays || 30} days, that earning is removed.` })}</span></li>
              <li className="flex gap-2"><span aria-hidden className="text-purple-600">•</span><span>{t('howPayoutsRule5', { days: summary.cooldownDays || 30, defaultValue: `Once ${summary.cooldownDays || 30} days have passed cleanly, the earning becomes available for your next payout.` })}</span></li>
              <li className="flex gap-2"><span aria-hidden className="text-purple-600">•</span><span>{t('howPayoutsRule6', 'If a payment is refunded or successfully disputed, its commission may be reversed — including commissions already paid, which may be deducted from future earnings.')}</span></li>
            </ul>
          </div>
        </details>

        {/* Referrals Table */}
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <div className="px-5 py-4 border-b">
            <h2 className="text-lg font-bold text-gray-900">Your Referrals</h2>
          </div>
          {referrals.length === 0 ? (
            <div className="p-8 text-center">
              <Link2 className="h-10 w-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No referrals yet</p>
              <p className="text-sm text-gray-400 mt-1">Share your referral link to start earning commission</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="helper-referrals-table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Business</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Type</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Status</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Plan</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Commission</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {referrals.map((r) => {
                    const cfg = STATUS_CONFIG[r.status] || STATUS_CONFIG.lead;
                    const Icon = cfg.icon;
                    const isRenewal = r.type === 'renewal_commission';
                    const isFounderLead = r.type === 'founder_lead';
                    return (
                      <tr key={r.id} className="hover:bg-gray-50" data-testid={`helper-referral-${r.id}`}>
                        <td className="px-5 py-3 font-medium text-gray-900">{r.businessName}</td>
                        <td className="px-5 py-3">
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded ${
                              isFounderLead
                                ? 'bg-purple-50 text-purple-700 border border-purple-200'
                                : isRenewal
                                  ? 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                                  : 'bg-gray-50 text-gray-700 border border-gray-200'
                            }`}
                            data-testid={`referral-type-${r.id}`}
                            title={
                              isFounderLead
                                ? t('founderLeadTooltip', 'Free 12-month signup — earns commission only if the business later upgrades or buys a boost.')
                                : isRenewal
                                  ? t('renewalCommissionTooltip', 'Commission earned because this business renewed their subscription')
                                  : t('initialCommissionTooltip', 'Commission earned for the original signup')
                            }
                          >
                            {isFounderLead ? t('founderLabel', 'Founder') : isRenewal ? t('renewalLabel', 'Renewal') : t('initialLabel', 'Initial')}
                          </span>
                        </td>
                        <td className="px-5 py-3">
                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full border ${cfg.color}`} data-testid={`referral-status-${r.id}`}>
                            <Icon className="h-3 w-3" />
                            {cfg.label}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-gray-600">{r.planLabel || (isFounderLead ? t('foundersPlanShort', '12-Month Free') : '-')}</td>
                        <td className="px-5 py-3">
                          {r.commissionAmount > 0 ? (
                            <div>
                              <span className="font-bold text-green-700" data-testid={`referral-commission-${r.id}`}>{r.commissionAmount.toFixed(2)} EUR</span>
                              {r.isAvailable ? (
                                <p className="text-[10px] text-emerald-600 font-medium mt-0.5" data-testid={`referral-available-${r.id}`}>{t('availableNow', 'Available')}</p>
                              ) : (typeof r.cooldownDaysLeft === 'number' && r.cooldownDaysLeft > 0 && r.status === 'pending_payout') ? (
                                <p className="text-[10px] text-amber-600 mt-0.5" data-testid={`referral-cooldown-${r.id}`}>
                                  {t('maturesInDays', { count: r.cooldownDaysLeft, defaultValue_one: `Matures in ${r.cooldownDaysLeft} day`, defaultValue_other: `Matures in ${r.cooldownDaysLeft} days` })}
                                  {r.estimatedPayoutDate && (
                                    <span className="block text-[10px] text-gray-400" data-testid={`referral-eta-${r.id}`}>
                                      ≈ {new Date(r.estimatedPayoutDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                                    </span>
                                  )}
                                </p>
                              ) : null}
                            </div>
                          ) : isFounderLead ? (
                            <p
                              className="text-[11px] text-purple-700/80 leading-snug max-w-[18rem]"
                              data-testid={`referral-founder-hint-${r.id}`}
                            >
                              {t(
                                'founderLeadEarningsHint',
                                'Earns 50% commission if this business later upgrades or buys a boost.'
                              )}
                            </p>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-5 py-3 text-gray-500">{r.createdAt?.slice(0, 10)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Payout details (helper-managed, admin-visible) */}
        <HelperPayoutDetails />

        {/* Commission note */}
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
          <p className="text-sm text-purple-800">
            {t('commissionHeadlineNew', 'Earn 50% of everything your referred businesses pay')} — {t('commissionHeadlineSub', 'subscriptions, renewals, boosts, and every future paid product.')}
          </p>
          <p className="text-xs text-purple-600 mt-1">
            {t('minPayoutHint', { amount: (summary.minPayoutEur || 20).toFixed(0), defaultValue: `Minimum €${(summary.minPayoutEur || 20).toFixed(0)} balance needed for payout.` })} {t('payoutCycleNote', { days: summary.cooldownDays || 30, defaultValue: `Payouts are reviewed after a ${summary.cooldownDays || 30}-day cooldown (refund window) and processed monthly by the admin team.` })}
          </p>
        </div>
      </div>
    </div>
  );
};

export default HelperDashboard;
